import type { Session } from "@supabase/supabase-js";
import { LoaderCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppLayout } from "./components/AppLayout";
import { supabase } from "./lib/supabase";
import { JobDetailPage } from "./routes/JobDetailPage";
import { JobsPage } from "./routes/JobsPage";
import { LoginPage } from "./routes/LoginPage";
import { SettingsPage } from "./routes/SettingsPage";
import { WorkspacePage } from "./routes/WorkspacePage";
import { VisualTestApp } from "./visual/VisualTestApp";

const visualTestMode = import.meta.env.VITE_VISUAL_TEST_MODE === "true";

function NotFound() {
  return (
    <div className="page-container not-found-page">
      <span className="kicker">404</span>
      <h1>页面不存在</h1>
      <p>这个地址没有对应的 SVG 工作台页面。</p>
      <a className="primary-button compact" href="/workspace">返回工作区</a>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(!visualTestMode);

  useEffect(() => {
    if (visualTestMode) return;
    let mounted = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setLoading(false);
    });
    return () => {
      mounted = false;
      data.subscription.unsubscribe();
    };
  }, []);

  if (visualTestMode) return <VisualTestApp />;
  if (loading) return <main className="loading-screen" aria-label="正在加载工作台"><LoaderCircle className="spin" size={28} /></main>;

  return (
    <Routes>
      <Route path="/login" element={<LoginPage authenticated={Boolean(session)} />} />
      <Route path="/" element={session ? <AppLayout session={session} /> : <Navigate to="/login" replace />}>
        <Route index element={<Navigate to="/workspace" replace />} />
        <Route path="workspace" element={<WorkspacePage />} />
        <Route path="jobs" element={<JobsPage />} />
        <Route path="jobs/:jobId" element={<JobDetailPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
